import { useDynamicLocale } from '@/hoslog-intl';
import { CalendarIcon, QuestionCircle } from '@/icons';
import { Clock } from '@/icons/clock';
import { HeartShapedEyes } from '@/icons/heart-shaped-eyes';
import { PoutFace } from '@/icons/pout-face';
import { SadFace } from '@/icons/sad-face';
import { formatDate } from '@/lib/dateFormatter';
import { formatDateJp } from '@/lib/dateFormatterJp';
import { ReviewPayload } from '@/services/faq/faq.mutation';
import { Loader2 } from 'lucide-react';
import { JSX } from 'react';
import { useRatingsPreview } from './hooks/useRatingsPreview';
import { HostIcon } from './HostIcon';
import { TagItem } from './TagItem';

type EmojiOption = {
  key: string;
  icon: JSX.Element;
  label: string;
};

export default function ReviewDetails({ reviewId }: ReviewPayload) {
  const { ratingPreview, loading } = useRatingsPreview({ reviewId });
  const { t, locale } = useDynamicLocale();
  const qnaRemarks = ratingPreview?.qnaRemarks ?? [];
  const intentRemarks = ratingPreview?.intentRemarks ?? [];
  const hostList = ratingPreview?.hostReviewRatings ?? [];
  const ratingScale = ratingPreview?.customerInfo?.ratingScale ?? 0;

  const emojiData: EmojiOption[] = [
    { key: 'YES', icon: <HeartShapedEyes />, label: t('faq.reviewRating.ratingDetailModel.options.satisfied') },
    { key: 'NEITHER', icon: <PoutFace />, label: t('faq.reviewRating.ratingDetailModel.options.average') },
    { key: 'NO', icon: <SadFace />, label: t('faq.reviewRating.ratingDetailModel.options.dissatisfied') },
  ];

  const emojiMap = emojiData.reduce<Record<string, JSX.Element>>((acc, item) => {
    acc[item.key] = item.icon;
    return acc;
  }, {});

  const getEmoji = (remarkType?: string) => {
    if (!remarkType) return emojiMap['NEITHER'] || null;
    const key = remarkType?.toUpperCase();
    return emojiMap[key] || null;
  };
  const otherTags = ratingPreview?.clubReviewRatings;

  const sortedHostList = [...hostList].sort((a, b) => {
    if (a.isRankedOne && !b.isRankedOne) return -1;
    if (!a.isRankedOne && b.isRankedOne) return 1;
    return 0;
  });

  return (
    <div>
      {loading ? (
        <div className="w-[318px] flex items-center justify-center">
          <Loader2 />
        </div>
      ) : (
        <div className="w-[318px] space-y-2">
          <div className="flex items-center gap-[5px]">
            <div className="w-[30px] h-[30px] shrink-0 overflow-hidden">
              <img
                src={ratingPreview?.customerInfo.customerImageResponse.path}
                alt={ratingPreview?.customerInfo.customerImageResponse.alt}
                className="rounded-full object-cover w-[30px] h-[30px]"
              />
            </div>
            <div>
              <p className="text-inquiry-foreground font-bold text-fine leading-[15.4px] break-all">
                {ratingPreview?.customerInfo.nickName}
              </p>
              <div className="flex space-x-1 items-center h-[11px]">
                <div className="flex h-[11px] text-fine text-warning leading-[10px]">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i}>{i < ratingScale ? '★' : '☆'}</span>
                  ))}
                </div>
                <p className="text-fine font-medium">{`${ratingScale}.0`}</p>
              </div>
            </div>
          </div>
          {qnaRemarks.length > 0 && (
            <div className="flex justify-between rounded-sm bg-review-highlight px-2 py-[6.26px]">
              <div className="flex flex-wrap items-center gap-2">
                {qnaRemarks.map((qnaRemark) => (
                  <div key={qnaRemark.qnaJpShortQuestion} className="flex gap-1">
                    <div className="whitespace-nowrap text-[8px] font-bold">{qnaRemark.qnaJpShortQuestion} :</div>
                    <div>{getEmoji(qnaRemark?.qnaEngAnswer)}</div>
                  </div>
                ))}
              </div>
              <div className="group relative">
                <QuestionCircle className="mt-[1px] size-2.5 shrink-0 text-foreground-subtle hover:cursor-pointer" />
                <div
                  className={`${locale === 'en' ? '-left-13' : '-left-7.5 w-[48px]'} absolute top-2.5 z-20 ml-0.5 mt-2 hidden h-[54px] scale-95 transform rounded-sm bg-card px-2 py-1.5 text-[7px] font-medium text-on-color shadow-2xl transition-all duration-200 group-hover:block group-hover:scale-100`}
                >
                  <div>
                    <div className="flex flex-col gap-[3px]">
                      {emojiData.map((option, index) => (
                        <div key={index} className="flex items-center gap-1">
                          {option.icon}
                          <div className="text-center font-medium leading-[140%] text-review-foreground text-[7px]">
                            {option.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="absolute right-3 top-0 h-[5px] w-[5px] -translate-y-1/2 rotate-45 bg-card"></div>
                </div>
              </div>
            </div>
          )}
          {(intentRemarks.length > 0 || ratingPreview?.customerInfo?.isCourseExtended) && (
            <div className="flex items-center gap-2">
              {intentRemarks.length > 0 && (
                <p className="text-fine font-bold">
                  {locale === 'ja' ? intentRemarks[0]?.intentJapaneseRemark : intentRemarks[0]?.intentEnglishRemark}
                </p>
              )}
              {ratingPreview?.customerInfo?.isCourseExtended && (
                <div className="rounded-xs bg-gradient-to-r from-review-gradient-start via-review-gradient-middle to-review-gradient-end px-1 text-[8px] font-medium text-review-background leading-100%">
                  {t('faq.reviewRating.ratingDetailModel.courseExtension')}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-start gap-x-0.5 gap-y-1">
            {otherTags?.map((tag, i) => (
              <TagItem key={i} name={locale === 'en' ? tag.englishRemark : tag.japaneseRemark} type={tag.remarkType} />
            ))}
          </div>
          <div className="flex flex-col gap-2 rounded-sm bg-review-surface px-3 py-1">
            <div className="leading-[100%] relative flex items-center gap-[7px] text-[9px] font-bold text-review-foreground">
              {t('faq.reviewRating.ratingDetailModel.title.hostTitle')}
            </div>
            <div className="inline-flex items-start justify-start gap-[3px]">
              {sortedHostList.slice(0, 8).map((host, i) => (
                <HostIcon
                  key={i}
                  imageUrl={host.hostImageResponse.path}
                  name={host.hostName}
                  isBest={host.isRankedOne}
                />
              ))}
            </div>
          </div>
          <div className="w-full flex justify-between text-[8px] leading-[11.2px] text-review-caption">
            <div className="flex gap-[3.95px] items-center">
              <CalendarIcon className="text-review-label w-[8.024px] h-2.25" />
              <p className="flex space-x-0.5">
                <p>
                  {t('faq.reviewRating.ratingDetailModel.visitedOn')}
                  {locale === 'en'
                    ? formatDate(ratingPreview?.customerInfo.visitingDateTime)
                    : formatDateJp(ratingPreview?.customerInfo.visitingDateTime)}
                </p>
                <p>{formatDate(ratingPreview?.customerInfo.visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1]}</p>
              </p>
            </div>
            <div className="flex gap-[3.88px] items-center">
              <Clock className="text-review-label w-[8.058px] h-2 mt-[1px]" />
              <p className="flex space-x-0.5">
                <p>
                  {t('faq.reviewRating.ratingDetailModel.commentedOn')}
                  {locale === 'en'
                    ? formatDate(ratingPreview?.customerInfo.endingDateTime)
                    : formatDateJp(ratingPreview?.customerInfo.endingDateTime)}
                </p>
                <p>{formatDate(ratingPreview?.customerInfo.endingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1]}</p>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
